/**
 * The attachments of an unfinished request (ADN-597). localStorage holds text
 * only, so the form's answers live there and the files it had attached live
 * here, in IndexedDB, which stores File objects as they are. One entry per
 * localStorage key, tagged with the id of the record it belongs to.
 *
 * Every call settles instead of throwing: storage can be unavailable (some
 * private windows), full, blocked or closed by the browser, and that must
 * only cost the user their attachments, never the form.
 */

/** localStorage keys of unfinished requests start with this. */
export const UNFINISHED_REQUEST_STORAGE_PREFIX = 'unfinishedRequest'

const DB_NAME = 'approveit-unfinished-requests'
const STORE_NAME = 'files'
const LOAD_TIMEOUT_MS = 3000

type StoredFiles = { id: string; files: Blob[] }

/**
 * - `loaded`: the files saved for the record.
 * - `missing`: none are stored for it.
 * - `unavailable`: storage failed or did not answer in time, so whatever is
 *   stored must be left alone: it may still be there.
 */
export type LoadedFiles = { status: 'loaded'; files: Blob[] } | { status: 'missing' } | { status: 'unavailable' }

let dbPromise: Promise<IDBDatabase> | null = null

const forgetDb = () => {
  dbPromise = null
}

const openDb = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'))
        return
      }
      const request = indexedDB.open(DB_NAME, 1)
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME)
      }
      request.onsuccess = () => {
        const db = request.result
        // Let another tab upgrade or delete the database instead of blocking it.
        db.onversionchange = () => {
          db.close()
          forgetDb()
        }
        // The browser can drop the connection (Safari in the background, site
        // data cleared). Reopen on the next call rather than fail every call.
        db.onclose = forgetDb
        resolve(db)
      }
      request.onerror = () => reject(request.error)
    })
    dbPromise.catch(forgetDb)
  }
  return dbPromise
}

const transaction = async (mode: IDBTransactionMode): Promise<IDBTransaction> => {
  const db = await openDb()
  try {
    return db.transaction(STORE_NAME, mode)
  } catch {
    // A connection closed without an event (InvalidStateError): reopen once.
    forgetDb()
    return (await openDb()).transaction(STORE_NAME, mode)
  }
}

const run = async <T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> => {
  const tx = await transaction(mode)
  return new Promise<T | undefined>((resolve, reject) => {
    const request = operation(tx.objectStore(STORE_NAME))
    tx.oncomplete = () => resolve(request ? request.result : undefined)
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

const settle = async <T>(promise: Promise<T>, fallback: T, action: string): Promise<T> => {
  try {
    return await promise
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[UnfinishedRequest] could not ${action} attachments`, error)
    return fallback
  }
}

/** Stores the record's files, or removes the entry when it has none. Resolves false when that failed. */
export const saveUnfinishedRequestFiles = (key: string, id: string, files: Blob[]): Promise<boolean> => {
  const entry: StoredFiles = { id, files }
  const write = files.length
    ? run('readwrite', (store) => store.put(entry, key))
    : run('readwrite', (store) => store.delete(key))
  return settle(
    write.then(() => true),
    false,
    'save',
  )
}

/** The files saved for record `id`. */
export const loadUnfinishedRequestFiles = (key: string, id: string): Promise<LoadedFiles> => {
  const load = run<StoredFiles | undefined>('readonly', (store) => store.get(key)).then(
    (entry): LoadedFiles =>
      entry?.id === id && Array.isArray(entry.files) ? { status: 'loaded', files: entry.files } : { status: 'missing' },
  )
  // An IndexedDB that never answers must not keep the form on a spinner.
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<LoadedFiles>((resolve) => {
    timer = setTimeout(() => resolve({ status: 'unavailable' }), LOAD_TIMEOUT_MS)
  })
  return settle<LoadedFiles>(Promise.race([load, timeout]), { status: 'unavailable' }, 'load').finally(() =>
    clearTimeout(timer),
  )
}

export const deleteUnfinishedRequestFiles = (key: string): Promise<void> =>
  settle(
    run('readwrite', (store) => store.delete(key)).then(() => undefined),
    undefined,
    'delete',
  )

/** Removes every stored attachment: on logout, with localStorage.clear(). */
export const clearUnfinishedRequestFiles = (): Promise<void> =>
  settle(
    run('readwrite', (store) => store.clear()).then(() => undefined),
    undefined,
    'clear',
  )

/**
 * Removes entries no record points at any more: left by a logout that cleared
 * localStorage without reaching this, or by a write that lost a race. One
 * read-write transaction: another tab's write waits for it, so an entry
 * cannot be judged stale and then replaced before it is deleted.
 */
export const sweepUnfinishedRequestFiles = (getRecordId: (key: string) => string | undefined): Promise<void> =>
  settle(
    transaction('readwrite').then(
      (tx) =>
        new Promise<void>((resolve, reject) => {
          const cursorRequest = tx.objectStore(STORE_NAME).openCursor()
          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result
            if (!cursor) return
            const entry = cursor.value as StoredFiles | undefined
            if (typeof cursor.key !== 'string' || !entry || entry.id !== getRecordId(cursor.key)) {
              cursor.delete()
            }
            cursor.continue()
          }
          tx.oncomplete = () => resolve()
          tx.onerror = () => reject(tx.error)
          tx.onabort = () => reject(tx.error)
        }),
    ),
    undefined,
    'sweep',
  )
